/* ==========================================================================
   idam-search-modal.js

   API-driven predictive search adapted from the idam-search component
   (registry/liquid/assets/idam-search.js) to run INSIDE the theme's existing
   search modal. The modal (search-modal.js) owns open/close/overlay/focus-trap,
   so this widget only handles: fetching suggestions from
   /api/v1/search/suggest, rendering products / trending / recent searches, and
   navigating to the results page on submit.
   ========================================================================== */
(function () {
  'use strict';

  var SUGGEST_CACHE_TTL_MS = 60 * 1000;
  var DEBOUNCE_MS = 250;
  var RECENT_KEY = 'idam-search:recent';
  var RECENT_MAX = 6;

  /* ----------------------------------------------------------------- utils */
  function moneyFromFormat(amount, format) {
    if (amount == null || amount === '' || isNaN(amount)) return '';
    var n = Number(amount);
    // Preserve cents when the price isn't a whole number (e.g. 22.99), but keep
    // whole prices clean (e.g. 599). Some stores return decimals, some don't.
    var hasDecimals = Math.round(n) !== n;
    var num = n.toLocaleString(undefined, {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2
    });
    // Pull the currency symbol/prefix from the shop's money_format (e.g. "$", "₹").
    var symbol = '';
    if (format && format.indexOf('{{') !== -1) {
      symbol = (format.match(/^[^{]*/) || [''])[0].trim();
    }
    return (symbol || '₹') + num;
  }

  function suggestImageUrl(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.url || image.src || '';
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function parseLines(value) {
    if (!value) return [];
    return value.split('\n').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function getRecent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
    catch (e) { return []; }
  }

  function pushRecent(term) {
    if (!term) return;
    try {
      var list = getRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      list.unshift(term);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* ignore */ }
  }

  /* ------------------------------------------------------------- component */
  function ModalSearch(root) {
    this.root = root;
    this.baseUrl = (root.getAttribute('data-backend-base-url') || '').replace(/\/$/, '');
    this.shopDomain = root.getAttribute('data-shop-domain') || '';
    this.resultsPath = root.getAttribute('data-results-path') || '/search';
    this.suggestLimit = parseInt(root.getAttribute('data-suggest-limit'), 10) || 5;
    this.moneyFormat = root.getAttribute('data-money-format') || '';
    this.showTrending = root.getAttribute('data-show-trending') !== 'false';
    this.trendingTerms = parseLines(root.getAttribute('data-trending-terms'));
    this.sendEvents = root.getAttribute('data-send-events') === 'true';

    this.inputs = Array.prototype.slice.call(root.querySelectorAll('[data-idam-search-input]'));
    this.clearBtns = Array.prototype.slice.call(root.querySelectorAll('[data-idam-search-clear]'));
    this.forms = Array.prototype.slice.call(root.querySelectorAll('[data-idam-search-form]'));
    this.bodyEmpty = root.querySelector('[data-idam-search-empty]');
    this.suggestWrap = root.querySelector('[data-idam-search-suggest]');
    this.suggestList = root.querySelector('[data-idam-search-suggest-list]');
    this.searchForBtn = root.querySelector('[data-idam-search-submit]');
    this.statusEl = root.querySelector('[data-idam-search-status]');
    this.trendingWrap = root.querySelector('[data-idam-search-trending]');
    this.trendingList = root.querySelector('[data-idam-search-trending-list]');
    this.recentWrap = root.querySelector('[data-idam-search-recent]');
    this.recentList = root.querySelector('[data-idam-search-recent-list]');

    this.cache = {};
    this.activeRequestId = 0;
    this.abortCtrl = null;
    this.term = '';
  }

  ModalSearch.prototype.init = function () {
    if (!this.baseUrl) {
      console.warn('[idam-search-modal] data-backend-base-url is empty; predictive search disabled.');
      return;
    }
    if (!this.inputs.length) return;

    var self = this;
    var debounced = debounce(function () { self.onQuery(); }, DEBOUNCE_MS);

    this.inputs.forEach(function (input) {
      input.addEventListener('input', function () {
        self.syncInputs(input.value);
        self.toggleClear();
        debounced();
      });
      // The modal focuses the input when it opens — render the empty state then.
      input.addEventListener('focus', function () { self.onQuery(); });
    });

    this.clearBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.syncInputs('');
        self.toggleClear();
        self.onQuery();
        var first = self.inputs[0];
        if (first) first.focus();
      });
    });

    // Native GET submit navigates to the results page; just record the term first.
    this.forms.forEach(function (form) {
      form.addEventListener('submit', function () {
        var term = self.currentValue();
        if (!term) return;
        pushRecent(term);
        if (self.sendEvents) self.track('search', { query: term });
      });
    });

    if (this.searchForBtn) {
      this.searchForBtn.addEventListener('click', function () { self.submit(self.currentValue()); });
    }

    // When the modal closes, reset back to the empty state for next time.
    document.addEventListener('searchmodal:close', function () {
      self.term = '';
      self.showEmpty();
    });

    this.toggleClear();
    if (this.currentValue()) this.onQuery();
    else this.showEmpty();
  };

  ModalSearch.prototype.currentValue = function () {
    return this.inputs.length ? this.inputs[0].value.trim() : '';
  };

  ModalSearch.prototype.syncInputs = function (value) {
    this.inputs.forEach(function (input) { if (input.value !== value) input.value = value; });
  };

  ModalSearch.prototype.toggleClear = function () {
    var has = this.currentValue().length > 0;
    this.clearBtns.forEach(function (b) { b.hidden = !has; });
  };

  /* ---- query handling --------------------------------------------------- */
  ModalSearch.prototype.onQuery = function () {
    var term = this.currentValue();
    this.term = term;
    this.toggleClear();

    if (!term) {
      this.showEmpty();
      return;
    }
    this.fetchSuggest(term);
  };

  ModalSearch.prototype.showEmpty = function () {
    if (this.suggestWrap) this.suggestWrap.hidden = true;
    if (this.statusEl) this.statusEl.hidden = true;
    if (this.bodyEmpty) this.bodyEmpty.hidden = false;
    this.renderEmptyState();
  };

  ModalSearch.prototype.renderEmptyState = function () {
    var self = this;
    if (this.showTrending && this.trendingWrap && this.trendingList) {
      if (this.trendingTerms.length) {
        this.trendingList.innerHTML = '';
        this.trendingTerms.forEach(function (t) { self.trendingList.appendChild(self.chip(t)); });
        this.trendingWrap.hidden = false;
      } else {
        this.trendingWrap.hidden = true;
      }
    }
    if (this.recentWrap && this.recentList) {
      var recent = getRecent();
      if (recent.length) {
        this.recentList.innerHTML = '';
        recent.forEach(function (t) { self.recentList.appendChild(self.chip(t)); });
        this.recentWrap.hidden = false;
      } else {
        this.recentWrap.hidden = true;
      }
    }
  };

  ModalSearch.prototype.chip = function (term) {
    var self = this;
    var li = document.createElement('li');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'idam-search__chip';
    btn.textContent = term;
    btn.addEventListener('click', function () {
      self.syncInputs(term);
      self.submit(term);
    });
    li.appendChild(btn);
    return li;
  };

  ModalSearch.prototype.fetchSuggest = function (term) {
    var self = this;
    var key = term.toLowerCase();
    var cached = this.cache[key];
    if (cached && (Date.now() - cached.t) < SUGGEST_CACHE_TTL_MS) {
      this.renderSuggest(cached.data, term);
      return;
    }

    if (this.bodyEmpty) this.bodyEmpty.hidden = true;
    if (this.suggestWrap) this.suggestWrap.hidden = true;
    if (this.statusEl) { this.statusEl.hidden = false; this.statusEl.textContent = 'Searching…'; }

    var reqId = ++this.activeRequestId;
    if (this.abortCtrl) this.abortCtrl.abort();
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    this.abortCtrl = ctrl;

    var url = this.baseUrl + '/api/v1/search/suggest?q=' + encodeURIComponent(term) + '&limit=' + this.suggestLimit;
    var opts = {
      method: 'GET',
      headers: { 'X-Shop-Domain': this.shopDomain, 'Accept': 'application/json' }
    };
    if (ctrl) opts.signal = ctrl.signal;

    fetch(url, opts)
      .then(function (r) { return r.json(); })
      .then(function (body) {
        if (reqId !== self.activeRequestId) return; // stale
        var data = (body && body.success && body.data) ? body.data : (body && body.data) || [];
        self.cache[key] = { t: Date.now(), data: data };
        self.renderSuggest(data, term);
        if (self.sendEvents) self.track('search_autocomplete', { query: term, count: data.length });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (reqId !== self.activeRequestId) return;
        self.renderSuggest([], term);
      });
  };

  ModalSearch.prototype.renderSuggest = function (items, term) {
    if (this.bodyEmpty) this.bodyEmpty.hidden = true;
    if (this.statusEl) this.statusEl.hidden = true;
    if (!this.suggestWrap || !this.suggestList) return;

    this.suggestList.innerHTML = '';

    if (!items || !items.length) {
      this.suggestWrap.hidden = true;
      if (this.statusEl) { this.statusEl.hidden = false; this.statusEl.textContent = 'No matches for “' + term + '”'; }
      this.showSearchFor(term);
      return;
    }

    var self = this;
    items.forEach(function (item) { self.suggestList.appendChild(self.suggestItem(item)); });
    this.suggestWrap.hidden = false;
    this.showSearchFor(term);
  };

  ModalSearch.prototype.showSearchFor = function (term) {
    if (!this.searchForBtn) return;
    this.searchForBtn.hidden = false;
    this.searchForBtn.textContent = 'Search for “' + term + '”';
    // Keep the button inside whichever group is currently visible.
    if (this.suggestWrap && this.suggestWrap.hidden && this.statusEl && !this.statusEl.hidden) {
      this.statusEl.appendChild(this.searchForBtn);
    } else if (this.suggestWrap) {
      this.suggestWrap.appendChild(this.searchForBtn);
    }
  };

  ModalSearch.prototype.suggestItem = function (item) {
    var self = this;
    var li = document.createElement('li');
    li.className = 'idam-search__suggest-item';
    li.setAttribute('role', 'option');

    var btn = document.createElement('button');
    btn.type = 'button';

    var imgUrl = suggestImageUrl(item.image);
    if (imgUrl) {
      var img = document.createElement('img');
      img.className = 'idam-search__suggest-thumb';
      img.src = imgUrl;
      img.alt = item.title || '';
      img.loading = 'lazy';
      btn.appendChild(img);
    } else {
      var ph = document.createElement('span');
      ph.className = 'idam-search__suggest-thumb';
      btn.appendChild(ph);
    }

    var meta = document.createElement('div');
    meta.className = 'idam-search__suggest-meta';

    var title = document.createElement('div');
    title.className = 'idam-search__suggest-title';
    title.textContent = item.title || '';
    meta.appendChild(title);

    var priceWrap = document.createElement('div');
    var price = (item.minPrice != null) ? item.minPrice : item.price;
    var priceSpan = document.createElement('span');
    priceSpan.className = 'idam-search__suggest-price';
    priceSpan.textContent = moneyFromFormat(price, this.moneyFormat);
    priceWrap.appendChild(priceSpan);
    if (item.compareAtPrice != null && Number(item.compareAtPrice) > Number(price)) {
      var cmp = document.createElement('span');
      cmp.className = 'idam-search__suggest-compare';
      cmp.textContent = moneyFromFormat(item.compareAtPrice, this.moneyFormat);
      priceWrap.appendChild(cmp);
    }
    meta.appendChild(priceWrap);

    btn.appendChild(meta);

    btn.addEventListener('click', function () {
      if (self.sendEvents) {
        self.track('search_autocomplete_click', { query: self.term, productHandle: item.handle, selectionType: 'product' });
      }
      if (item.handle) {
        window.location.href = '/products/' + item.handle;
      } else {
        self.submit(item.title || self.term);
      }
    });

    li.appendChild(btn);
    return li;
  };

  ModalSearch.prototype.submit = function (term) {
    term = (term || '').trim();
    if (!term) return;
    pushRecent(term);
    if (this.sendEvents) this.track('search', { query: term });
    var sep = this.resultsPath.indexOf('?') === -1 ? '?' : '&';
    window.location.href = this.resultsPath + sep + 'q=' + encodeURIComponent(term);
  };

  ModalSearch.prototype.track = function (name, payload) {
    if (!this.baseUrl) return;
    try {
      var body = JSON.stringify(Object.assign({ event: name }, payload || {}));
      fetch(this.baseUrl + '/api/v1/search/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shop-Domain': this.shopDomain },
        body: body,
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* ignore */ }
  };

  /* ------------------------------------------------------------------ boot */
  function boot() {
    var roots = document.querySelectorAll('[data-idam-search]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__idamSearchModalBooted) return;
      root.__idamSearchModalBooted = true;
      new ModalSearch(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
